/**
 * Host-agnostic Complex-Login identity fan-out.
 *
 * Given the multi-set editing model and ONE resource key (asset / web-app), it
 * builds and submits ONE broker payload PER identity, each carrying that
 * identity's own `loginUrl` (per-identity `login_url` is LOCKED 2026-06-16 —
 * see types.ts `CredentialIdentity`). The actual network call is INJECTED via
 * `submitOne`, so both hosts (the asset drawer's `CredentialModals`/
 * `AddCredentialModal` stack and the seed wizard's `useAddSeedSubmit`) reuse
 * this exact fan-out without this module importing host hooks, React, or axios.
 *
 * Per-identity failure is isolated: a thrown `submitOne` becomes an `ok:false`
 * result and does NOT abort the other identities (partial failure is
 * per-identity). Results preserve identity order, which drives the STACKED
 * per-identity recorder-progress view (R-7 / LOCKED #3).
 */

import {
  type BrokerPayloadBody,
  buildBrokerPayload,
} from './buildBrokerPayload';
import { identityToSingle } from './complexLoginIdentities';
import { runBounded } from './runBounded';
import {
  type ComplexLoginMultiFormData,
  type CredentialIdentity,
  CredentialType,
} from './types';

/** Per-identity submit outcome, in identity order. */
export interface IdentitySubmitResult {
  id: string;
  /** The model label (clean — NOT the privilege-marked wire label). */
  label: string;
  ok: boolean;
  conversationId?: string;
  error?: unknown;
}

/**
 * `submitOne` performs ONE broker POST for a single identity and resolves with
 * the spawned recorder `conversationId` (when the broker returned one). Its
 * first parameter is exactly what `buildBrokerPayload` returns for
 * COMPLEX_LOGIN — a {@link BrokerPayloadBody} (`Type`/`Format`/`Parameters`);
 * the caller wraps `Category`/`Operation`/`ResourceKey` on before POSTing.
 */
export async function submitComplexLoginIdentities(args: {
  multi: ComplexLoginMultiFormData;
  submitOne: (
    brokerPayload: BrokerPayloadBody,
    identity: CredentialIdentity
  ) => Promise<{ conversationId?: string }>;
  limit?: number;
}): Promise<IdentitySubmitResult[]> {
  const { multi, submitOne, limit } = args;

  return runBounded(
    multi.identities,
    async (identity): Promise<IdentitySubmitResult> => {
      try {
        // identityToSingle re-injects the typed totpSecret + privilege marker so
        // buildBrokerPayload(COMPLEX_LOGIN) emits today's exact wire shape, with
        // this identity's own loginUrl as login_url. The COMPLEX_LOGIN case of
        // buildBrokerPayload ignores `isWebService` (it always emits web-auth),
        // so the flag value is irrelevant here.
        const single = identityToSingle(multi, identity);
        const brokerPayload = buildBrokerPayload(
          { type: CredentialType.COMPLEX_LOGIN, data: single },
          { isWebService: false }
        );
        const { conversationId } = await submitOne(brokerPayload, identity);
        // label is the CLEAN model label (identity.label), never the privilege-
        // marked wire label that identityToSingle put on `single.label`.
        return {
          id: identity.id,
          label: identity.label,
          ok: true,
          conversationId,
        };
      } catch (error) {
        return { id: identity.id, label: identity.label, ok: false, error };
      }
    },
    limit
  );
}
